
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CampLocation, LocationVersion } from "@/types/campLocation";
import { UserInvitation } from "@/types/userInvitation";
import { Organization } from "@/types/organization";
import LocationFormHeader from "@/components/admin/locations/components/LocationFormHeader";
import { DetailsTab } from "./DetailsTab";
import { OfferingsAndSessionsTab } from "@/components/admin/locations/components/OfferingsAndSessionsTab";
import WelcomeSessionsTab from "../../components/WelcomeSessionsTab";
import { PhotosTabSection } from "@/components/admin/locations/LocationFormSections/PhotosTabSection";
import { UseFormReturn } from "react-hook-form";
import { LocationFormData } from "@/components/admin/locations/schemas/locationFormSchema";
import { publishLocationChanges } from "@/services/locationVersion/publishLocationService";
import { getFirestore } from "firebase/firestore";
import { createLogger } from "@/utils/logUtils";
import { markInvitationAsViewed } from "@/services/userInvitationService";
import { ensureInviteAuth, persistInviteToAccountIfNeeded } from "@/auth/inviteAuth";
import { getAuth } from "firebase/auth";
import { useAuth } from "@/context/AuthContext";

console.log("[WelcomeTabsManager] Importing WelcomeSessionsTab from:", "../../components/WelcomeSessionsTab");

const logger = createLogger("WelcomeTabsManager");

interface WelcomeTabsManagerProps {
  location: CampLocation;
  invitation: UserInvitation;
  latestDraft?: LocationVersion | null;
  organizationData?: Organization | null;
  form: UseFormReturn<LocationFormData>;
  isSaving: boolean;
  isSavingOrgData: boolean;
  isDirty: boolean;
  orgFieldsChanged: boolean;
  changeDescription: string;
  setChangeDescription: (value: string) => void;
  lastSavedTime: Date | null;
  handleSaveWithOrgInfo: () => Promise<void>;
  aboutOrgText: string;
  setAboutOrgText: (value: string) => void;
  staffingText: string;
  setStaffingText: (value: string) => void;
  versionNumber: number;
  activeTab?: string;
  onTabChange?: (value: string) => void;
}

export const WelcomeTabsManager: React.FC<WelcomeTabsManagerProps> = ({
  location,
  invitation,
  latestDraft,
  organizationData,
  form,
  isSaving,
  isSavingOrgData,
  isDirty,
  orgFieldsChanged,
  changeDescription,
  setChangeDescription,
  lastSavedTime,
  handleSaveWithOrgInfo,
  aboutOrgText,
  setAboutOrgText,
  staffingText,
  setStaffingText,
  versionNumber,
  activeTab: externalActiveTab,
  onTabChange: externalOnTabChange
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState("details");
  const activeTab = externalActiveTab ?? internalActiveTab;

  // Apply invitation token if present and then strip it from URL.
  useEffect(() => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("token");
    if (!token) return;
    
    let cancelled = false;
    
    const claimWhenReady = async () => {
      try {
        // Wait for auth to be ready before claiming invitation
        const auth = getAuth();
        
        // Always wait for auth to be initialized - Firebase v9+ fires onAuthStateChanged when ready
        console.log("WelcomeTabsManager: Waiting for auth state to be determined...");
        await new Promise<void>(resolve => {
          const unsubscribe = auth.onAuthStateChanged(() => {
            unsubscribe();
            resolve();
          });
        });
        
        if (cancelled) return;
        
        console.log("WelcomeTabsManager: Auth state ready, claiming invitation...");
        await ensureInviteAuth(token);
        
        // Store token for later account persistence
        localStorage.setItem("kb_invite_token", token);
        console.log("WelcomeTabsManager: Successfully claimed invitation");
        
      } catch (e) {
        console.error("Failed to claim invitation", e);
      }
      
      if (!cancelled) {
        // Clean up URL after claiming (or attempting to claim)
        const clean = new URL(window.location.href);
        clean.searchParams.delete("token");
        window.history.replaceState({}, "", clean.toString());
      }
    };
    
    claimWhenReady();
    
    return () => {
      cancelled = true;
    };
  }, []);

  // If user signs into a real account, persist durable access.
  useEffect(() => {
    persistInviteToAccountIfNeeded().catch((e) => console.error("persistInviteToAccountIfNeeded failed", e));
  }, []);

  // Track tab changes and update invitation view count
  const handleTabChange = (value: string) => {
    if (externalOnTabChange) {
      externalOnTabChange(value);
    } else {
      setInternalActiveTab(value);
    }
    
    // Increment view count and update lastViewedAt when tab changes
    if (invitation && invitation.token) {
      logger.info(`Tab changed to ${value}, updating invitation view count for token: ${invitation.token}`);
      markInvitationAsViewed(invitation.token)
        .then((success) => {
          if (success) {
            logger.info("Successfully incremented view count");
          } else {
            logger.info("Failed to increment view count");
          }
        })
        .catch((error) => {
          logger.error("Error updating view count:", error);
        });
    }
  };

  // Add a function to handle publishing after a program is saved
  const handleProgramSaved = async (locationId: string): Promise<void> => {
    try {
      logger.info("Auto-publishing location after program save:", locationId);
      
      // Get userId from invitation
      const userId = invitation?.id || invitation?.token || "token-user";
      
      // Call the publish function with showToasts set to false for welcome screen
      const db = getFirestore();
      const result = await publishLocationChanges(db, locationId, userId, {
        showToasts: false,
        refreshVersionHistory: () => {
          // Dispatch an event to refresh version history
          window.dispatchEvent(new CustomEvent("refreshVersionHistory"));
        }
      });
      
      if (result) {
        logger.info("Auto-publish after program save successful:", result);
      } else {
        logger.error("Auto-publish after program save failed");
      }
      
      return;
    } catch (error) {
      logger.error("Error during auto-publish after program save:", error);
      return;
    }
  };

  const UserNoticeBanner: React.FC = () => {
    const auth = getAuth();
    const [line1, setLine1] = useState<string>("");
    const [line2, setLine2] = useState<string>("");
    const [showLogout, setShowLogout] = useState<boolean>(false);
    const { signOut } = useAuth();

    const handleLogout = async () => {
      try {
        // KB-3994: the context sign-out, not firebase's. It also revokes the
        // kidsbooked.com session and stops the apex SSO door re-adopting it,
        // both in this page and on the /login reload below.
        await signOut?.();
        // Clear any stored invitation token
        localStorage.removeItem("kb_invite_token");
        // Redirect to login page
        window.location.href = "/login";
      } catch (error) {
        console.error("Error signing out:", error);
      }
    };
    
    useEffect(() => {
      let mounted = true;
      
      const updateUserInfo = async () => {
        const user = auth.currentUser;
        if (!user) {
          if (mounted) {
            setLine1("");
            setLine2("");
            setShowLogout(false);
          }
          return;
        }
        
        try {
          if (user.isAnonymous) {
            const token = await user.getIdTokenResult();
            const ms = (token.claims as any)?.inviteExpiresAt as number | undefined;
            if (mounted) {
              if (ms) {
                const dateStr = new Date(ms).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  timeZone: "America/New_York",
                });
                setLine1(`Guest access expires ${dateStr}. `);
              } else {
                setLine1("Editing as guest. ");
              }
              setLine2("create-account-link");
              setShowLogout(false);
            }
          } else {
            if (mounted) {
              setLine1("Editing as:");
              setLine2(user.email || user.uid);
              setShowLogout(true);
            }
          }
        } catch (e) {
          // Fallback
          if (mounted) {
            if (user.isAnonymous) {
              setLine1("Editing as guest");
              setLine2("create-account-link");
              setShowLogout(false);
            } else {
              setLine1("Editing as:");
              setLine2(user.email || user.uid);
              setShowLogout(true);
            }
          }
        }
      };

      // Initial update
      updateUserInfo();
      
      // Listen for auth state changes
      const unsubAuth = auth.onAuthStateChanged(() => {
        updateUserInfo();
      });
      
      // Also listen for ID token/claims changes so we update when invite claims land
      const unsubToken = auth.onIdTokenChanged(() => {
        updateUserInfo();
      });
      
      return () => {
        mounted = false;
        unsubAuth();
        unsubToken();
      };
    }, []);

    if (!line1 && !line2) return null;
    return (
      <div className="w-full bg-yellow-100 border-b border-yellow-300">
        <div className="max-w-screen-xl mx-auto py-2 px-3">
          <div className={`text-sm leading-tight text-foreground ${showLogout ? 'text-right' : 'text-center'}`}>
            <span className="font-medium">{line1}</span>
            {" "}
            <span>
              {line2 === "create-account-link" ? (
                <Link
                  to="/create-account"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Create account to retain access
                </Link>
              ) : (
                <>
                  {line2}
                  {showLogout && (
                    <>
                      {" ("}
                      <button
                        onClick={handleLogout}
                        className="text-blue-600 hover:text-blue-700 underline"
                      >
                        Logout
                      </button>
                      {")"}
                    </>
                  )}
                </>
              )}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
      <div className="sticky top-0 z-20 bg-white">
        <UserNoticeBanner />
        <TabsList className="w-full justify-start border-b rounded-none bg-[#14992D]">
          <TabsTrigger value="details" className="text-white data-[state=active]:text-[#14992D] data-[state=active]:bg-white">Details</TabsTrigger>
          <TabsTrigger value="photos" className="text-white data-[state=active]:text-[#14992D] data-[state=active]:bg-white">
            Photos
          </TabsTrigger>
          <TabsTrigger value="programs" className="text-white data-[state=active]:text-[#14992D] data-[state=active]:bg-white">
            Programs
          </TabsTrigger>
          <TabsTrigger value="sessions" className="text-white data-[state=active]:text-[#14992D] data-[state=active]:bg-white">
            Sessions
          </TabsTrigger>
        </TabsList>
      </div>
      
      {/* Show header with unsaved changes below the tabs when on details tab */}
      {activeTab === "details" && <div className="sticky top-20 z-10 bg-white pb-0 border-b">
          <LocationFormHeader 
            locationId={location.id} 
            versionNumber={versionNumber} 
            versionId={latestDraft?.versionId} 
            isSaving={isSaving || isSavingOrgData} 
            savedStatus={isDirty || orgFieldsChanged ? "unsaved" : "saved"} 
            isDirty={isDirty || orgFieldsChanged} 
            changeDescription={changeDescription} 
            onChangeDescriptionChange={setChangeDescription} 
            lastSavedTime={lastSavedTime} 
            onManualSave={handleSaveWithOrgInfo} 
            isWelcomeScreen={true} 
          />
        </div>}
      
      <div className="p-4">
        <TabsContent value="details" className="mt-0">
          <DetailsTab 
            form={form}
            location={location}
            onSubmit={handleSaveWithOrgInfo}
            organizationData={organizationData}
            aboutOrgText={aboutOrgText}
            setAboutOrgText={setAboutOrgText}
            staffingText={staffingText}
            setStaffingText={setStaffingText}
          />
        </TabsContent>

        <TabsContent value="photos" className="mt-0">
          <PhotosTabSection 
            locationId={location.id}
            location={location}
            onSave={handleSaveWithOrgInfo}
            saving={isSaving}
          />
        </TabsContent>
        
        <TabsContent value="programs" className="mt-0">
          <OfferingsAndSessionsTab 
            location={location} 
            isWelcomeScreen={true}
            invitation={invitation}
            onProgramSaved={handleProgramSaved}
            organizationData={organizationData}
          />
        </TabsContent>
        
        <TabsContent value="sessions" className="mt-0">
          <WelcomeSessionsTab location={location} invitation={invitation} />
        </TabsContent>
      </div>
    </Tabs>
  );
};
